import React from 'react'
import getCloudinaryUrl from '@/utils/getCloudinaryUrl'
import USCanonicalPopularDestinationsSubHeader from '@/components/USCanonical/USCanonicalPopularDestinationsSubHeader'

import FeaturedLocations from '@/components/FeaturedLocations'
import useImageProps from '@/hooks/useImageProps'
import useLinkProps from '@/hooks/useLinkProps'
import { isNonEmptyString } from '@/utils/checks'
import {
  isLocationPageType,
  isTaxonomyPageType
} from '@/domain/useCases/pageTypeCheck'
import { PageType } from '@/types/models/PageType.model'
import { Term } from '@/types/models/Term.model'
import Carousel from '@/components/MiniCarousel/Carousel'
import MiniCard from '@/components/MiniCarousel/MiniCard'
import normalizeSearchState from '@/domain/search/normalizeSearchState'
import { navigateToBrowsePageWithQueryParams } from '@/domain/search/navigateToBrowsePageWithQueryParams'
import mapPostgresTermWithAlogliaFacetType from '@/domain/term/mapPostgresTermTypeWithAloglia'
import { IndexType } from '@/domain/seo/constants'
import { getIndexTypeForTerm } from '@/domain/term/useCases/getIndexTypeForTerm'

interface Location {
  slug: string
  title: string
  hero_image: string
}

interface TopLocationsSectionProps {
  pageType?: PageType
  relatedLocations: Location[]
  global: boolean
  inFeed?: boolean
  term?: Term
}

const TopLocationsSection: React.FC<TopLocationsSectionProps> = ({
  pageType,
  relatedLocations,
  global = true,
  inFeed = false,
  term
}) => {
  return (
    <div
      key="top-locations-section"
      className="my-8 md:my-10"
    >
      <div className="flex flex-col pb-4">
        <h3 className="font-FieldsVariable text-lg font-medium text-gray-1100">
          {isLocationPageType(pageType)
            ? 'Top Treatment Locations'
            : 'Popular Destinations'}
        </h3>
        {!global && (
          <USCanonicalPopularDestinationsSubHeader slug={term?.slug} />
        )}
      </div>

      {inFeed ? (
        <TopLocationsMiniCarousel
          relatedLocations={relatedLocations}
          pageType={pageType}
          term={term}
        />
      ) : (
        <FeaturedLocations
          locations={relatedLocations
            .filter((location) => location && location.hero_image)
            .map((location) => ({
              imageProps: useImageProps({
                aspectRatio: 'landscape',
                loading: 'lazy',
                src: getCloudinaryUrl(
                  location.hero_image.split('/').pop(),
                  'e_gen_restore/q_25,w_480,h_360,c_fill'
                )
              }),
              linkProps: useLinkProps({
                href: isLocationPageType(pageType)
                  ? `/${location.slug}/`
                  : `/${location.slug}/${term?.slug}/`,
                title: `${location.title} Treatment`
              }),
              text: location.title
            }))}
        />
      )}
    </div>
  )
}

const TopLocationsMiniCarousel = ({ relatedLocations, pageType, term }) => {
  const indexType = term ? getIndexTypeForTerm(term) : IndexType.FULL_INDEX

  const cards = relatedLocations
    .filter((location) => location && location.hero_image)
    .map((location) => ({
      count: 0,
      bgImage: getCloudinaryUrl(
        location.hero_image.split('/').pop(),
        'e_gen_restore/q_25,w_480,h_360,c_fill'
      ),
      title: location.title,
      location: location.title,
      slug:
        isTaxonomyPageType(pageType) && isNonEmptyString(term?.slug)
          ? `${location.slug}/${term?.slug}`
          : location.slug,
      showCount: false,
      anchorTitle: `${location.title}`,

      useBrowsePage:
        indexType === IndexType.NO_INDEX || indexType === IndexType.NO_FACET
    }))

  const onClick = (card) => {
    if (card.useBrowsePage) {
      navigateToBrowsePageWithQueryParams(
        normalizeSearchState({
          refinementList: {
            locations: [card.title],
            [mapPostgresTermWithAlogliaFacetType(term.type)]: term.title
          }
        })
      )
    }
  }

  return (
    <Carousel
      id="top-locations-section"
      itemsCount={cards.length}
      topArrowPosition="8"
      smallArrows
      titleClassName={'leading-8'}
    >
      {cards.map((card) => (
        <div
          className="mr-4"
          key={card.slug}
        >
          {card.useBrowsePage ? (
            <MiniCard
              card={card}
              labelSize="large"
              onCardClick={() => onClick(card)}
            />
          ) : (
            <a
              href={`/${card.slug}/`}
              title={card.anchorTitle}
              className="block"
            >
              <MiniCard
                card={card}
                labelSize="large"
              />
            </a>
          )}
        </div>
      ))}
    </Carousel>
  )
}

export default TopLocationsSection
